import React, { useEffect, useState, useCallback } from "react";
import { t } from "i18next";
import i18n from "i18n";
import { CustomInput } from "ui/Input";
import CustomButton, { TextButton } from "ui/Button";
import { COLOR, ICON_NAMES } from "utils/constant";
import styles from "styles.module.css";
import { Languages } from "lucide-react";
import CustomModal from "ui/modal/Modal";
import { getCatchSignerUserApi } from "api/userAgreement";

import CustomDropDownList from "ui/dropDown";
import { getGetInternationalCountryCodeApi } from "api/common";
import { DropdownRenderItem } from "ui/dropdownItemList";
import { InternationalCountryCode } from "interface/common";
import { ListItemProps } from "@progress/kendo-react-dropdowns";
import { RegisterRecreationalForm } from "interface/recreationalFishers";
import { recreationalVesselRegistrationApi } from "api/vessel";
import { ErrorModal } from "ui/ErrorInfo";
import { useInfoStore } from "store/error";
import { CustomError } from "interface/index";

const defaultValue: RegisterRecreationalForm = {
	registrationNumber: "",
	name: "",
	usernameValid: false,
	ownerFirstName: "",
	ownerLastName: "",
	ownerAddress: "",
	ownerZip: "",
	ownerCity: "",
	ownerContactEmail: "",
	ownerBusinessRegistrationNumber: "",
	phonePrefix: "",
	ownerPhoneNumber: "",
};
const nullToEmpty = (value: string | undefined | null) => {
	if (!value || value === "Null") {
		return "";
	}
	return value;
};
function RegisterRecreationalFishers() {
	const [form, setForm] = useState<RegisterRecreationalForm>(defaultValue);
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);
	const [showError, setShowError] = useState<boolean>(false);
	const [countryCodes, setCountryCodes] = React.useState<
		InternationalCountryCode[]
	>([]);
	const [countryCodesFilter, setCountryCodesFilter] = React.useState<
		InternationalCountryCode[]
	>([]);
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);
	const [tempUsername, setTempUsername] = useState<string>("");
	const [isRegistering, setIsRegistering] = useState(false);

	const { setError: setInforError, setInfo } = useInfoStore();

	const isValidEmail = (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	};

	const validateForm = useCallback(() => {
		const newErrors: { [key: string]: string } = {};
		if (!form.ownerFirstName.trim())
			newErrors.vesselOwnerFirstname = t("Missing") + " " + t("FirstName");
		if (!form.ownerLastName.trim())
			newErrors.vesselOwnerLastname = t("Missing") + " " + t("LastName");
		if (!form.ownerBusinessRegistrationNumber.trim())
			newErrors.vesselOwnerIdentityNumber =
				t("Missing") + " " + t("OrganizationNumber");
		if (!form.ownerAddress.trim())
			newErrors.vesselOwnerAddress = t("Missing") + " " + t("Address");
		if (!form.ownerZip.trim())
			newErrors.vesselOwnerPostalCode = t("Missing") + " " + t("PostalCode");
		if (!form.ownerCity.trim())
			newErrors.vesselOwnerCity = t("Missing") + " " + t("City");
		if (!form.phonePrefix.trim())
			newErrors.phonePrefix = t("Missing") + " " + t("CountryCode");
		if (!form.ownerPhoneNumber.trim())
			newErrors.vesselOwnerPhoneNumber = t("Missing") + " " + t("PhoneNumber");
		if (!form.ownerContactEmail.trim())
			newErrors.vesselOwnerEmail = t("Missing") + " " + t("Email");
		else if (!isValidEmail(form.ownerContactEmail))
			newErrors.vesselOwnerEmail = t("InvalidEmail");
		if (!form.registrationNumber.trim())
			newErrors.vesselRegNo = t("Missing") + " " + t("VesselRegistrationNo");
		if (!form.name.trim())
			newErrors.vesselName = t("Missing") + " " + t("VesselName");

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [form]);

	useEffect(() => {
		getGetInternationalCountryCodeApi()
			.then((response) => {
				setCountryCodes(response);
				setCountryCodesFilter(response);
			})
			.catch((error) => {
				console.log(error);
			});
	}, [form, showError, validateForm]);

	useEffect(() => {
		if (showError) validateForm();
	}, [form, showError, validateForm]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const handleInputChange = (
		field: keyof RegisterRecreationalForm,
		value: string,
	) => {
		setForm((previousForm) => ({ ...previousForm, [field]: value }));
	};

	const handleSubmit = () => {
		if (isRegistering) return;

		const valid = validateForm();
		if (!valid) {
			setShowError(true);
			return;
		}

		proceedSubmit();
	};

	const proceedSubmit = async () => {
		if (!form) {
			setShowError(true);
			return;
		}

		setIsRegistering(true);
		try {
			await recreationalVesselRegistrationApi({
				...form,
				ownerName: `${form.ownerFirstName} ${form.ownerLastName}`.trim(),
				ownerContactPhone: `${form.phonePrefix}${form.ownerPhoneNumber}`.trim(),
			});

			setInfo({
				show: true,
				title: t("Success"),
				infoType: "Success",
				description: t("VesselRegistered"),
			});

			setForm(defaultValue);
			setTempUsername("");
			setErrors({});
			setShowError(false);
		} catch (error: any) {
			setInforError(new CustomError(""));
		} finally {
			setIsRegistering(false);
		}
	};

	const handleLanguageSwitch = (language: string) => {
		i18n.changeLanguage(language);
		setCurrentLanguage(language);
	};

	const handleFilterChange = (e: string) => {
		const filtered = countryCodes.filter(
			(code) =>
				code.internationalDialing.startsWith(e) ||
				code.country.toLowerCase().startsWith(e.toLowerCase()), //||
		);
		const sorted = filtered.sort((a, b) => a.country.localeCompare(b.country));
		setCountryCodesFilter(sorted);
		// if (filtered.length === 0) {
		// 	setCountryCodesFilter(countryCodes);
		// }
	};

	const isValidUsername = (fieldValue?: string) => {
		if (!fieldValue) {
			return false;
		}

		const normalizedValue = fieldValue.trim().toUpperCase();
		const signaturePattern = /^\d{6}[A-Z]{6}$/;
		return signaturePattern.test(normalizedValue);
	};

	const validateUsername = () => {
		setErrors({});
		getCatchSignerUserApi(tempUsername)
			.then((response) => {
				if (response.isValid) {
					const _username = response.signUser.username.trim();
					if (
						_username.toLocaleLowerCase() !==
						tempUsername.trim().toLocaleLowerCase()
					) {
						return;
					}

					const signer = response.signUser;
					setForm((prev) => ({
						...prev,
						usernameValid: true,
						ownerFirstName: signer.firstname,
						ownerLastName: signer.lastname,
						ownerAddress: nullToEmpty(signer.address),
						ownerZip: nullToEmpty(signer.zipCode),
						ownerCity: nullToEmpty(signer.location),
						ownerContactEmail: signer.email,
						ownerPhoneNumber: signer.phoneNumber,
					}));
				} else {
					console.error("Invalid catch signer user");
					setForm((previousForm) => ({
						...previousForm,
						usernameValid: false,
					}));
					setErrors((prev) => ({
						...prev,
						usernameInValid:
							t("Invalid") + " " + t("CatchSign") + " " + t("Username"),
					}));
				}
			})
			.catch((error) => {
				console.error("Error fetching catch signer user:", error);
				setForm((previousForm) => ({
					...previousForm,
					usernameValid: false,
				}));
				setErrors((prev) => ({
					...prev,
					usernameInValid:
						t("Invalid") + " " + t("CatchSign") + " " + t("Username"),
				}));
			});
	};

	const invalidateUsername = () => {
		setForm((previousForm) => ({
			...previousForm,
			usernameValid: false,
		}));
		// setForm((prev) => ({
		// 	...(prev ?? defaultForm),
		// 	userFirstName: "",
		// 	userLastName: "",
		// 	userEmail: "",
		// 	userPhone: "",
		// }));
	};

	return (
		<div className={`${styles.PageContainer} user-agreement-page`}>
			<div
				className="user-agreement-language-switch"
				style={{
					display: "flex",
					alignItems: "center",
					flexWrap: isMobileView ? "wrap" : "nowrap",
					gap: "10px",
					justifyContent: "center",
					marginBottom: "20px",
				}}
			>
				<div
					className="user-agreement-language-option"
					style={{
						display: "flex",
						alignItems: "center",
						gap: "10px",
						cursor: "pointer",
						padding: "8px 12px",
						borderRadius: "4px",
						backgroundColor:
							currentLanguage === "no" ? "#f0f0f0" : "transparent",
						border:
							currentLanguage === "no" ? "2px solid #007bff" : "1px solid #ccc",
					}}
					onClick={() => handleLanguageSwitch("no")}
				>
					<Languages color={currentLanguage === "no" ? "#007bff" : "#666"} />
					<span
						style={{
							color: currentLanguage === "no" ? "#007bff" : "#666",
							fontWeight: currentLanguage === "no" ? "bold" : "normal",
						}}
					>
						Norwegian
					</span>
				</div>
				<div
					className="user-agreement-language-option"
					style={{
						display: "flex",
						alignItems: "center",
						gap: "10px",
						cursor: "pointer",
						padding: "8px 12px",
						borderRadius: "4px",
						backgroundColor:
							currentLanguage === "en" ? "#f0f0f0" : "transparent",
						border:
							currentLanguage === "en" ? "2px solid #007bff" : "1px solid #ccc",
					}}
					onClick={() => handleLanguageSwitch("en")}
				>
					<Languages color={currentLanguage === "en" ? "#007bff" : "#666"} />
					<span
						style={{
							color: currentLanguage === "en" ? "#007bff" : "#666",
							fontWeight: currentLanguage === "en" ? "bold" : "normal",
						}}
					>
						English
					</span>
				</div>
			</div>
			<div className="page-header" style={{ textAlign: "center" }}>
				<legend className={styles.BoxHeadingLarge}>
					{t("RegisterRecreationalFishers")}
				</legend>
			</div>

			<div className="user-agreement-content">
				<div className="user-agreement-section">
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "20px",
							marginBottom: "20px",
						}}
					>
						<div className={styles.BoxHeadingMedium} style={{ margin: 0 }}>
							{`${t("User")}`}
						</div>
					</div>

					<div className="user-agreement-two-column-row">
						<div className="user-agreement-column">
							<div
								className="user-agreement-user-fields"
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "20px",
								}}
							>
								<div>
									<div style={{ display: "flex", gap: "10px" }}>
										<CustomInput
											label={t("CatchSign") + " " + t("Username")}
											placeholder={t("CatchSign") + " " + t("Username")}
											value={tempUsername}
											max={12}
											onChange={(value) => {
												if (
													value.trim().toUpperCase() !==
													tempUsername.trim().toUpperCase()
												) {
													setErrors((prev) => ({
														...prev,
														usernameInValid: "",
													}));
													invalidateUsername();
												}
												setTempUsername(value.trim().toUpperCase());
											}}
											inputStyle={{
												width: "100%",

												borderColor: errors.usernameInValid ? "red" : undefined,
											}}
										/>
										<div style={{ marginTop: "24px" }}>
											{form.usernameValid ? (
												<img
													src={`icon/${ICON_NAMES.CheckGreen}.svg`}
													alt={"username valid"}
													width={30}
													height={30}
												/>
											) : (
												<CustomButton
													label={t("Validate")}
													bgColor={COLOR.Green}
													color="white"
													onClick={validateUsername}
													disabled={!isValidUsername(tempUsername.trim())}
													btnStyle={
														isMobileView
															? { width: "100%" }
															: { height: "32px" }
													}
												/>
											)}
										</div>
									</div>
									{errors.usernameInValid && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.usernameInValid}
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="user-agreement-column user-agreement-column-spacer"></div>
					</div>
				</div>

				<div className="user-agreement-section">
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "20px",
							marginBottom: "20px",
						}}
					>
						<div className={styles.BoxHeadingMedium} style={{ margin: 0 }}>
							{`${t("VesselOwner")}`}
						</div>
					</div>

					<div className="user-agreement-two-column-row">
						<div className="user-agreement-column">
							<div
								className="user-agreement-user-fields"
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "20px",
								}}
							>
								<div>
									<CustomInput
										label={t("FirstName")}
										placeholder={t("FirstName")}
										value={form.ownerFirstName}
										readOnly={!form.usernameValid}
										onChange={(value) =>
											handleInputChange("ownerFirstName", value)
										}
										inputStyle={{
											width: "100%",
											borderColor: errors.vesselOwnerFirstname
												? "red"
												: undefined,
										}}
									/>
									{errors.vesselOwnerFirstname && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.vesselOwnerFirstname}
										</div>
									)}
								</div>

								<div>
									<CustomInput
										label={t("LastName")}
										placeholder={t("LastName")}
										value={form.ownerLastName}
										readOnly={!form.usernameValid}
										onChange={(value) =>
											handleInputChange("ownerLastName", value)
										}
										inputStyle={{
											width: "100%",
											borderColor: errors.vesselOwnerLastname
												? "red"
												: undefined,
										}}
									/>
									{errors.vesselOwnerLastname && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.vesselOwnerLastname}
										</div>
									)}
								</div>
								<div>
									<CustomInput
										label={t("NationalIdentityNumber")}
										placeholder={t("NationalIdentityNumber")}
										value={form.ownerBusinessRegistrationNumber}
										readOnly={!form.usernameValid}
										onChange={(value) =>
											handleInputChange(
												"ownerBusinessRegistrationNumber",
												value,
											)
										}
										isError={!!errors.vesselOwnerIdentityNumber}
									/>
									{errors.vesselOwnerIdentityNumber && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.vesselOwnerIdentityNumber}
										</div>
									)}
								</div>
								<div>
									<CustomInput
										label={t("Address")}
										placeholder={t("Address")}
										value={form.ownerAddress}
										readOnly={!form.usernameValid}
										onChange={(value) =>
											handleInputChange("ownerAddress", value)
										}
										isError={!!errors.vesselOwnerAddress}
									/>
									{errors.vesselOwnerAddress && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.vesselOwnerAddress}
										</div>
									)}
								</div>
								<div>
									<CustomInput
										label={t("PostalCode")}
										placeholder={t("PostalCode")}
										value={form.ownerZip}
										readOnly={!form.usernameValid}
										onChange={(value) => handleInputChange("ownerZip", value)}
										isError={!!errors.vesselOwnerPostalCode}
									/>
									{errors.vesselOwnerPostalCode && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.vesselOwnerPostalCode}
										</div>
									)}
								</div>
								<div>
									<CustomInput
										label={t("City")}
										placeholder={t("City")}
										value={form.ownerCity}
										readOnly={!form.usernameValid}
										onChange={(value) => handleInputChange("ownerCity", value)}
										isError={!!errors.vesselOwnerCity}
									/>
									{errors.vesselOwnerCity && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.vesselOwnerCity}
										</div>
									)}
								</div>
								<div>
									<div
										className="user-agreement-phone-row"
										style={{
											display: "flex",
											flexDirection: isMobileView ? "column" : "row",
											gap: "10px",
										}}
									>
										<div style={{ width: isMobileView ? "100%" : "50%" }}>
											<span style={{ paddingBottom: "4px", display: "block" }}>
												{t("CountryCode")}
											</span>
											<CustomDropDownList
												data={countryCodesFilter}
												//data={[]}
												dataItemKey="internationalDialing"
												textField="internationalDialing"
												filterable={true}
												// label={t("CountryCode")}
												// placeholder={t("CountryCode")}
												handleFilterChange={(e) => {
													if (e.filter) {
														handleFilterChange(e.filter.value);
													}
												}}
												value={{
													internationalDialing: form.phonePrefix,
												}}
												onChange={(e) => {
													const selectedPrefix = e.value.internationalDialing;
													handleInputChange("phonePrefix", selectedPrefix);
													// console.log("changed", e);
												}}
												onOpen={() => {}}
												disabled={!form.usernameValid}
												defaultItem={{ internationalDialing: t("Choose") }}
												itemRender={(
													li: any, // React.ReactElement<HTMLLIElement>,
													itemProps: ListItemProps,
												) => {
													const data: InternationalCountryCode =
														itemProps.dataItem;

													const item = (
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "12px",
															}}
														>
															<span style={{ width: "50px" }}>
																{data.internationalDialing}
															</span>
															<span>{data.country}</span>
														</div>
													);

													return React.cloneElement(li, li.props, item);
												}}
												showError={!!errors.phonePrefix}
											/>
										</div>
										<div style={{ width: isMobileView ? "100%" : "50%" }}>
											<CustomInput
												label={t("PhoneNumber")}
												placeholder={t("PhoneNumber")}
												value={form.ownerPhoneNumber}
												type="number"
												readOnly={!form.usernameValid}
												onChange={(value) => {
													handleInputChange("ownerPhoneNumber", value);
												}}
												inputStyle={{
													width: "100%",
													borderColor: errors.vesselOwnerPhoneNumber
														? "red"
														: undefined,
												}}
											/>
										</div>
									</div>
									{errors.vesselOwnerPhoneNumber && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.vesselOwnerPhoneNumber}
										</div>
									)}
									{errors.phonePrefix && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.phonePrefix}
										</div>
									)}
								</div>
								<div>
									<CustomInput
										label={t("Email")}
										placeholder={t("Email")}
										value={form.ownerContactEmail}
										readOnly={!form.usernameValid}
										onChange={(value) =>
											handleInputChange("ownerContactEmail", value)
										}
										inputStyle={{
											width: "100%",
											borderColor: errors.vesselOwnerEmail ? "red" : undefined,
										}}
									/>
									{errors.vesselOwnerEmail && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.vesselOwnerEmail}
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="user-agreement-column user-agreement-column-spacer"></div>
					</div>
				</div>

				<div className="user-agreement-section">
					<h2
						style={{ marginBottom: "20px" }}
						className={styles.BoxHeadingMedium}
					>
						{t("Vessel")}
					</h2>

					<div
						className="user-agreement-two-column-row"
						style={{ marginBottom: "20px" }}
					>
						<div
							className="user-agreement-column"
							style={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: "10px",
							}}
						>
							<>
								<div
									className="user-agreement-grid"
									style={{
										display: "grid",
										gridTemplateColumns: isMobileView ? "1fr" : "1fr 1fr",
										gap: 12,
									}}
								>
									<CustomInput
										label={t("VesselRegistrationNo")}
										value={form.registrationNumber}
										onChange={(v) => handleInputChange("registrationNumber", v)}
										isError={!!errors.vesselRegNo}
										placeholder={t("VesselRegistrationNo")}
									/>
									<CustomInput
										label={t("VesselName")}
										value={form.name}
										onChange={(v) => handleInputChange("name", v)}
										isError={!!errors.vesselName}
										placeholder={t("VesselName")}
									/>
								</div>
							</>
						</div>
						<div className="user-agreement-column user-agreement-column-spacer"></div>
					</div>
				</div>
				<ErrorModal />
				<div
					className="user-agreement-actions-row"
					style={{
						marginTop: "40px",
						display: "flex",
						justifyContent: "center",
					}}
				>
					<CustomButton
						label={isRegistering ? t("Loading") : t("ConfirmRegistration")}
						bgColor={COLOR.Green}
						color="white"
						disabled={!form.usernameValid || isRegistering}
						onClick={handleSubmit}
						btnStyle={isMobileView ? { width: "100%" } : undefined}
					/>
				</div>
			</div>
		</div>
	);
}

export default RegisterRecreationalFishers;
